import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";


const API_URL = `${API_BASE_URL}/api`;

const LeaveService = {
    getAllLeaveRequests: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/leaveRequests`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching all leave requests:", error);
            throw error;
        }
    },

    getUserLeaveRequests: async (empNo, companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/leaveRequests/employee/${empNo}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching user leave requests:", error);
            throw error;
        }
    },
    
    getReportingEmployeesLeaveRequests: async (managerId, companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/leaveRequests/manager/${managerId}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching reporting employees' leave requests:", error);
            throw error;
        }
    },

    addLeaveRequest: async (leaveRequest, companyId, token, userType) => {
        try {
            const response = await axios.post(`${API_URL}/leaveRequests`, leaveRequest, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error adding leave request:", error);
            throw error;
        }
    },

    updateLeaveStatus: async (id, status, companyId, token, userType, approvedBy = null) => {
        try {
            const response = await axios.put(`${API_URL}/leaveRequests/${id}/status`, { 
                status,
                approvedBy: approvedBy
            }, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error updating leave status:", error);
            throw error;
        }
    },

    getLeaveBalances: async (empNo, companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/leaveBalances/${empNo}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching leave balances:", error);
            throw error;
        }
    },

    initializeLeaveBalances: async (empNo, companyId, token, userType) => {
        try {
            const response = await axios.post(`${API_URL}/leaveBalances`, { empNo }, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error initializing leave balances:", error);
            throw error;
        }
    },

    getEmployees: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/employees`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching employees:", error);
            throw error;
        }
    },

    getReportingRelationships: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/employees/structure`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data.reportingRelationships || [];
        } catch (error) {
            console.error("Error fetching reporting relationships:", error);
            throw error;
        }
    }
};

const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

const LeaveForm = ({ onLeaveAdded, currentUser, isAdmin, employees, leaveBalances, companyId, token, userType }) => {
    const [form, setForm] = useState({
        empNo: "",
        leaveType: "Sick Leave",
        startDate: "",
        endDate: "",
        reason: "",
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (currentUser && !isAdmin) {
            setForm(prev => ({ ...prev, empNo: currentUser.empNo }));
        }
    }, [currentUser, isAdmin]);

    const calculateLeaveDays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        
        try {
            const startDate = new Date(form.startDate);
            const endDate = new Date(form.endDate);
            
            if (endDate < startDate) {
                setError("End date cannot be before start date");
                return;
            }
            
            const leaveDays = calculateLeaveDays(form.startDate, form.endDate);
            
            let employeeName = "";
            let employeeNumber = "";
            
            if (isAdmin) {
                const selectedEmployee = employees.find(emp => emp.empNo === form.empNo);
                if (!selectedEmployee) {
                    setError("Please select an employee");
                    return;
                }
                employeeName = selectedEmployee.name;
                employeeNumber = form.empNo;
            } else {
                employeeName = currentUser.name;
                employeeNumber = currentUser.empNo;
            }
            
            const leaveRequest = {
                empNo: employeeNumber,
                name: employeeName,
                leaveType: form.leaveType,
                startDate: form.startDate,
                endDate: form.endDate,
                reason: form.reason,
                leaveDays: leaveDays,
                status: "Pending",
                submittedAt: new Date().toISOString(),
                submittedBy: currentUser.email
            };
            
            await LeaveService.addLeaveRequest(leaveRequest, companyId, token, userType);
            setForm({ 
                ...form, 
                leaveType: "Sick Leave", 
                startDate: "", 
                endDate: "", 
                reason: "" 
            });
            
            setSuccess("Leave request submitted successfully");
            onLeaveAdded();
        } catch (error) {
            console.error("Error submitting leave request:", error);
            if (error.response && error.response.data && error.response.data.error) {
                setError(error.response.data.error);
            } else {
                setError("Failed to submit leave request");
            }
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-gray-700 mb-4">Submit Leave Request</h2>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p>{error}</p>
                </div>
            )}
            
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    <p>{success}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {isAdmin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                        <select
                            value={form.empNo}
                            onChange={(e) => setForm({ ...form, empNo: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="">Select Employee</option>
                            {employees.map((employee) => (
                                <option key={employee.id} value={employee.empNo}>
                                    {employee.name} ({employee.empNo})
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                        <select
                            value={form.leaveType}
                            onChange={(e) => setForm({ ...form, leaveType: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="Sick Leave">Sick Leave</option>
                            <option value="Casual Leave">Casual Leave</option>
                            <option value="Earned Leave">Earned Leave</option>
                        </select>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={form.startDate}
                            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={form.endDate}
                            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea
                        value={form.reason}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Briefly explain the reason for your leave request"
                        required
                    />
                </div>
                
                {!isAdmin && leaveBalances && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold mb-2">Your Leave Balances:</h3>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <span className="block text-sm text-gray-600">Sick Leave</span>
                                <span className="font-bold text-lg">{leaveBalances.sickLeave} days</span>
                            </div>
                            <div>
                                <span className="block text-sm text-gray-600">Casual Leave</span>
                                <span className="font-bold text-lg">{leaveBalances.casualLeave} days</span>
                            </div>
                            <div>
                                <span className="block text-sm text-gray-600">Earned Leave</span>
                                <span className="font-bold text-lg">{leaveBalances.earnedLeave} days</span>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="flex justify-end">
                    <button 
                        type="submit"
                        className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
                    >
                        Submit Leave Request
                    </button>
                </div>
            </form>
        </div>
    );
};

const LeaveList = ({ leaveRequests, onStatusUpdate, isAdmin, isManagerView = false, currentUser }) => {
    const handleApprove = (id) => {
        onStatusUpdate(id, "Approved", currentUser?.id);
    };

    const handleReject = (id) => {
        onStatusUpdate(id, "Rejected", currentUser?.id);
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto mb-6">
            <h3 className="text-xl font-bold text-gray-700 mb-4">
                {isAdmin 
                    ? "All Leave Requests" 
                    : isManagerView 
                        ? "My Team's Leave Requests" 
                        : "My Leave Requests"}
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full table-auto text-gray-700 rounded-lg">
                    <thead>
                        <tr className="bg-blue-600 text-white">
                            {(isAdmin || isManagerView) && <th className="p-3 text-left">Employee</th>}
                            <th className="p-3 text-left">Type</th>
                            <th className="p-3 text-left">Duration</th>
                            <th className="p-3 text-left">Days</th>
                            <th className="p-3 text-left">Reason</th>
                            <th className="p-3 text-center">Status</th>
                            {(isAdmin || isManagerView) && <th className="p-3 text-left">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {leaveRequests.length > 0 ? (
                            leaveRequests.map((request) => (
                                <tr
                                    key={request.id}
                                    className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                                >
                                    {(isAdmin || isManagerView) && <td className="p-3 border-b border-gray-200">{request.name}</td>}
                                    <td className="p-3 border-b border-gray-200">{request.leaveType}</td>
                                    <td className="p-3 border-b border-gray-200">
                                        {formatDate(request.startDate)} to {formatDate(request.endDate)}
                                    </td>
                                    <td className="p-3 border-b border-gray-200">{request.leaveDays}</td>
                                    <td className="p-3 border-b border-gray-200">{request.reason}</td>
                                    <td className="p-3 border-b border-gray-200 text-center">
                                        <span 
                                            className={`px-2 py-1 rounded-full text-sm ${
                                                request.status === "Approved" 
                                                    ? "bg-green-100 text-green-800" 
                                                    : request.status === "Rejected"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-yellow-100 text-yellow-800"
                                            }`}
                                        >
                                            {request.status}
                                        </span>
                                    </td>
                                    {(isAdmin || isManagerView) && request.status === "Pending" && (
                                        <td className="p-3 border-b border-gray-200">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApprove(request.id)}
                                                    className="bg-green-100 text-green-800 px-3 py-1 rounded hover:bg-green-200"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleReject(request.id)}
                                                    className="bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    {(isAdmin || isManagerView) && request.status !== "Pending" && (
                                        <td className="p-3 border-b border-gray-200">-</td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={(isAdmin || isManagerView) ? 7 : 6} className="p-3 text-center text-gray-500">
                                    No leave requests found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const LeavePage = () => {
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [teamLeaveRequests, setTeamLeaveRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [leaveBalances, setLeaveBalances] = useState(null);
    const [reportingRelationships, setReportingRelationships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, userType, companyId, logout } = useAuth();
    const token = localStorage.getItem('token');
    
    const isAdmin = userType === 'user';
    const hasDirectReports = user && reportingRelationships.some(rel => rel.toId === user.id);

    const fetchReportingRelationships = useCallback(async () => {
        try {
            const relationshipsData = await LeaveService.getReportingRelationships(companyId, token, userType);
            setReportingRelationships(relationshipsData);
            return relationshipsData;
        } catch (error) {
            console.error("Error fetching reporting relationships:", error);
            setError("Failed to fetch organizational structure. Please try again later.");
            return [];
        }
    }, [companyId, token, userType]);

    const fetchLeaveRequests = useCallback(async () => {
        try {
            setLoading(true);
            let data;
            
            if (isAdmin) {
                data = await LeaveService.getAllLeaveRequests(companyId, token, userType);
            } else if (user) {
                data = await LeaveService.getUserLeaveRequests(user.empNo, companyId, token, userType);
            }
            
            setLeaveRequests(data || []);
            setError(null);
        } catch (error) {
            console.error("Error fetching leave requests:", error);
            setError("Failed to fetch leave requests. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [isAdmin, user, companyId, token, userType]);

    const fetchTeamLeaveRequests = useCallback(async () => {
        if (!user || isAdmin) return [];
        
        try {
            const data = await LeaveService.getReportingEmployeesLeaveRequests(user.id, companyId, token, userType);
            setTeamLeaveRequests(data || []);
            return data;
        } catch (error) {
            console.error("Error fetching team leave requests:", error);
            return [];
        }
    }, [user, isAdmin, companyId, token, userType]);
    
    const fetchEmployees = useCallback(async () => {
        try {
            const response = await LeaveService.getEmployees(companyId, token, userType);
            setEmployees(response);
        } catch (error) {
            console.error("Error fetching employees:", error);
        }
    }, [companyId, token, userType]);
    
    const fetchLeaveBalances = useCallback(async () => {
        if (!user || isAdmin) return;
        
        try {
            const balances = await LeaveService.getLeaveBalances(user.empNo, companyId, token, userType);
            setLeaveBalances(balances);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                const newBalances = await LeaveService.initializeLeaveBalances(user.empNo, companyId, token, userType);
                setLeaveBalances(newBalances);
            } else {
                console.error("Error fetching leave balances:", error);
            }
        }
    }, [user, isAdmin, companyId, token, userType]);
    
    useEffect(() => {
        const initializeData = async () => {
            if (!companyId) {
                logout();
                return;
            }

            if (user) {
                const relationships = await fetchReportingRelationships();
                await fetchLeaveRequests();
                
                if (isAdmin) {
                    await fetchEmployees();
                } else {
                    await fetchLeaveBalances();
                    
                    if (relationships.some(rel => rel.toId === user.id)) {
                        await fetchTeamLeaveRequests();
                    }
                }
                setLoading(false);
            } else {
                setLoading(false);
            }
        };
        
        initializeData();
    }, [
        user, 
        isAdmin, 
        companyId,
        fetchLeaveRequests, 
        fetchLeaveBalances, 
        fetchEmployees, 
        fetchReportingRelationships,
        fetchTeamLeaveRequests,
        logout
    ]);

    const handleStatusUpdate = async (id, status, approvedBy = null) => {
        try {
            await LeaveService.updateLeaveStatus(id, status, companyId, token, userType, approvedBy);
            
            await fetchLeaveRequests();
            
            if (hasDirectReports && !isAdmin) {
                await fetchTeamLeaveRequests();
            }
        } catch (error) {
            console.error("Error updating leave status:", error);
            setError("Failed to update leave status. Please try again later.");
        }
    };

    if (loading) {
        return (
            <div className="w-full p-6 flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
                <p className="text-white text-xl">Loading...</p>
            </div>
        );
    }

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Leave Management</h1>
            {companyId ? (
                <>
                    {user && (
                        <>
                            {isAdmin && (
                                <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                                    <h3 className="text-xl font-bold text-gray-700 mb-4">Admin Privileges</h3>
                                    <p className="text-gray-600">You have admin access to manage leave requests and approve/reject employee requests.</p>
                                </div>
                            )}

                            {!isAdmin && (
                                <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                                    <h3 className="text-xl font-bold text-gray-700 mb-4">Employee View</h3>
                                    <p className="text-gray-600">
                                        Welcome, {user.name}. You can submit leave requests and view your leave history below.
                                        {hasDirectReports && " You have team members reporting to you and can approve their leave requests."}
                                    </p>
                                </div>
                            )}

                            <LeaveForm 
                                onLeaveAdded={fetchLeaveRequests}
                                currentUser={user}
                                isAdmin={isAdmin}
                                employees={employees}
                                leaveBalances={leaveBalances}
                                companyId={companyId}
                                token={token}
                                userType={userType}
                            />

                            {error && (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-4xl mx-auto">
                                    <p>{error}</p>
                                </div>
                            )}

                            <LeaveList
                                leaveRequests={leaveRequests}
                                onStatusUpdate={handleStatusUpdate}
                                isAdmin={isAdmin}
                                currentUser={user}
                            />

                            {hasDirectReports && !isAdmin && teamLeaveRequests.length > 0 && (
                                <LeaveList
                                    leaveRequests={teamLeaveRequests}
                                    onStatusUpdate={handleStatusUpdate}
                                    isAdmin={false}
                                    isManagerView={true}
                                    currentUser={user}
                                />
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            )}
        </div>
    );
};

export default LeavePage;